import { z } from 'zod';

/** `{ zh, en }` label pair used for titles, objective labels and hints. */
export const LocalizedTextSchema = z.object({
  zh: z.string().min(1),
  en: z.string().min(1),
});
export type LocalizedText = z.infer<typeof LocalizedTextSchema>;

/** A spoken phrase with an optional romanisation and English gloss. */
export const SpokenPhraseSchema = z.object({
  text: z.string().min(1),
  romanization: z.string().optional(),
  english: z.string().optional(),
});
export type SpokenPhrase = z.infer<typeof SpokenPhraseSchema>;

/** One side of the conversation: its wire key, UI labels and prose noun. */
export const ScenarioRoleSchema = z.object({
  key: z.string().min(1),
  labelZh: z.string().min(1),
  labelEn: z.string().min(1),
  noun: z.string().min(1),
});
export type ScenarioRole = z.infer<typeof ScenarioRoleSchema>;

export const ScenarioLanguageSchema = z.object({
  /** Rendered into prompts: "Always respond in {language} only." */
  speak: z.string().min(1),
  /** Rendered into prompts: "never {avoid.join(' and ')}". */
  avoid: z.array(z.string()).default([]),
  bcp47: z.string().optional(),
  /** Label for the romanisation field, e.g. "jyutping". */
  romanization: z.string().optional(),
});
export type ScenarioLanguage = z.infer<typeof ScenarioLanguageSchema>;

export const ScenarioTierSchema = z.object({
  code: z.string().min(1),
  labelZh: z.string().min(1),
  labelEn: z.string().min(1),
});
export type ScenarioTier = z.infer<typeof ScenarioTierSchema>;

export const ScenarioPersonaSchema = z.object({
  roleBrief: z.string().min(1),
  successRule: z.string().min(1),
  failRule: z.string().min(1),
});
export type ScenarioPersona = z.infer<typeof ScenarioPersonaSchema>;

export const ScenarioRulesSchema = z.object({
  conversationFlow: z.array(z.string().min(1)).min(1),
  speaking: z.array(z.string().min(1)).min(1),
});
export type ScenarioRules = z.infer<typeof ScenarioRulesSchema>;

/** One "check topic" the learner must complete, in order. */
export const ScenarioObjectiveStepSchema = z.object({
  key: z.string().min(1),
  label: LocalizedTextSchema,
  hint: LocalizedTextSchema,
  example: SpokenPhraseSchema.optional(),
  /** Evidence rule judged by the checkpoint classifier. */
  completeWhen: z.string().min(1),
});
export type ScenarioObjectiveStep = z.infer<typeof ScenarioObjectiveStepSchema>;

/** Optional per-scenario prompt overrides; the engine ships defaults. */
export const ScenarioPromptsSchema = z.object({
  opening: z.string().optional(),
  nudge: z.string().optional(),
  closingPass: z.string().optional(),
  closingFail: z.string().optional(),
  /** Last in-character reply before the scenario closes. */
  finalReply: z.string().optional(),
});
export type ScenarioPrompts = z.infer<typeof ScenarioPromptsSchema>;

/** Optional overrides of the checkpoint tool descriptions. */
export const ScenarioToolCopySchema = z.object({
  advanceDescription: z.string().optional(),
  failDescription: z.string().optional(),
  endDescription: z.string().optional(),
});
export type ScenarioToolCopy = z.infer<typeof ScenarioToolCopySchema>;

export const DemoReportSchema = z.object({
  pronunciation: z.number().min(0).max(100),
  vocabulary: z.number().min(0).max(100),
  grammar: z.number().min(0).max(100),
  summaryZh: z.string(),
  summaryEn: z.string(),
  recommendedZh: z.string(),
  recommendedEn: z.string(),
});
export type DemoReport = z.infer<typeof DemoReportSchema>;

/** Content used by the non-realtime fallback. */
export const ScenarioDemoSchema = z.object({
  replies: z.array(SpokenPhraseSchema).default([]),
  report: DemoReportSchema.optional(),
  /** Instructions for the non-realtime turn generator (`ai.ts`). */
  chatPrompt: z.string().optional(),
  /** Instructions for the non-realtime conversation report (`ai.ts`). */
  reportPrompt: z.string().optional(),
});
export type ScenarioDemo = z.infer<typeof ScenarioDemoSchema>;

/**
 * Optional per-scenario session limits. These override the values in
 * `ChatPolicySettings.limits` for this scenario only (e.g. a harder tier gets
 * a longer session). Omitted fields fall back to the policy defaults.
 */
export const ScenarioLimitsSchema = z.object({
  maxTurns: z.number().int().positive().optional(),
  maxDurationMs: z.number().int().positive().optional(),
  silenceLimitMs: z.number().int().positive().optional(),
});
export type ScenarioLimits = z.infer<typeof ScenarioLimitsSchema>;

/** The full server-side theme. Never sent to the browser in one piece. */
export const ScenarioTemplateSchema = z.object({
  schemaVersion: z.string().default('1.0'),
  id: z.string().min(1),
  title: LocalizedTextSchema,
  tier: ScenarioTierSchema,
  /** Free string; the UI maps it to an icon and falls back to a default. */
  icon: z.string().min(1),
  sceneImage: z.string().optional(),
  language: ScenarioLanguageSchema,
  roles: z.object({
    ai: ScenarioRoleSchema,
    learner: ScenarioRoleSchema,
  }),
  persona: ScenarioPersonaSchema,
  opener: SpokenPhraseSchema,
  rules: ScenarioRulesSchema,
  objectiveSteps: z.array(ScenarioObjectiveStepSchema).min(1),
  prompts: ScenarioPromptsSchema.default({}),
  /** Subject line for the checkpoint classifier's system prompt. */
  classifierContext: z.string().min(1),
  tools: ScenarioToolCopySchema.default({}),
  /** Reasons the model may call `fail_scenario` with (server-only reasons excluded). */
  failReasons: z.array(z.enum(['gave_up', 'could_not_progress', 'off_task'])).min(1),
  demo: ScenarioDemoSchema.default({ replies: [] }),
  limits: ScenarioLimitsSchema.optional(),
});
export type ScenarioTemplate = z.infer<typeof ScenarioTemplateSchema>;

/**
 * The wire projection of a template: everything the browser needs to render
 * the scenario picker, the scene and the objective checklist — and nothing
 * that would let a learner game the pass/fail rules.
 */
export const ClientScenarioSchema = z.object({
  id: z.string(),
  title: LocalizedTextSchema,
  tier: ScenarioTierSchema,
  icon: z.string(),
  sceneImage: z.string().optional(),
  language: ScenarioLanguageSchema,
  roles: z.object({
    ai: ScenarioRoleSchema,
    learner: ScenarioRoleSchema,
  }),
  opener: SpokenPhraseSchema,
  objectiveSteps: z.array(ScenarioObjectiveStepSchema),
});
export type ClientScenario = z.infer<typeof ClientScenarioSchema>;

/** Strip a template down to the client view. */
export function toClientScenario(template: ScenarioTemplate): ClientScenario {
  return {
    id: template.id,
    title: template.title,
    tier: template.tier,
    icon: template.icon,
    sceneImage: template.sceneImage,
    language: template.language,
    roles: template.roles,
    opener: template.opener,
    objectiveSteps: template.objectiveSteps,
  };
}

/** Template-aware, engine-shipped defaults for the checkpoint prompt copy. */
export const DEFAULT_PROMPTS: Required<ScenarioPrompts> = {
  opening:
    'Speak your opening line now in {language} only, in character: greet {learnerNoun} briefly and ask what they would like to eat. You may say exactly 「{opener}」 or a natural equivalent. Keep it to 1-2 sentences.',
  nudge:
    'The learner has been quiet for a while. Ask in {language} only, briefly and kindly, whether they are still there. One short sentence only.',
  closingPass:
    'The scenario has ended with a successful result. Speak ONE short, warm closing statement in {language} only (1-2 sentences): thank {learnerNoun} and say goodbye. Do not ask any further questions.',
  closingFail:
    'The scenario has ended without completing the objective. Speak ONE short, encouraging closing statement in {language} only (1-2 sentences): thank them kindly and encourage them to try again another time. Do not ask any further questions.',
  finalReply:
    'The learner has just completed the final objective, but their last message still deserves a proper in-character answer before the scene ends. Speak ONE short reply in {language} only that answers it directly — for example, if they asked for the bill, tell them the total and thank them. Do not ask any further questions. Never mention objectives, steps, tools or assessment. Then call end_conversation. If you have already answered their last message in a previous turn, do not repeat yourself — just call end_conversation.',
};

export const DEFAULT_TOOL_COPY = {
  advanceDescription:
    'REQUIRED checkpoint for progressing to the next objective. Call it the INSTANT the learner completes the current objective step — for example, the moment they finished ordering and you confirmed it, call this before asking anything about the next task. Do not wait for the next topic to come up. The call should preferably contain no spoken words; at most one short acknowledgement may accompany it, never any mention of objectives, steps, or tools. The result privately tells you the next objective; use it to steer your next spoken line.',
  failDescription:
    'Finish the scenario with a FAIL result because the learner failed to communicate. Call this at ANY instant — even mid-scenario — when the learner cannot make themselves understood after a few simple attempts, explicitly gives up, or is hopelessly stuck. Prefer a silent call; never narrate the call, result, or rationale.',
  endDescription:
    'Finish the scenario with a PASS result. Call this ONLY after ALL objective steps are complete (advance_to_next_objective has reported all objectives complete) AND after you have spoken the final in-character reply that answers the learner\'s last message — for example, if they asked for the bill, the total has been said. Never call it early and never call it instead of that reply. Prefer a silent call; never narrate the call, result, or rationale.',
} as const;
